import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { searchSettingRefs } from "@/api/one_c_pickers";
import * as api from "@/api/system_settings";
import { PickerField } from "@/components/pickers/PickerField";
import { savedPickerValue, type PickerRow } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toastError } from "@/lib/api-errors";
import type { SystemSettingsView } from "@/types/api";

/**
 * Технические ссылки 1С — хозоперации, состояния заказов, валюта, единица.
 *
 * ⚠️ Ради этой карточки всё и заводилось. Предопределённые элементы получают
 * GUID при развёртывании конфигурации, то есть у каждой базы они СВОИ, а
 * заполнить их можно было только миграцией (намеренно только нашей базе) или
 * сид-скриптом. У второй инсталляции шесть штук остались пустыми, и разделы
 * упирались в «не задана ссылка 1С» — притом что задать её было НЕЧЕМ.
 *
 * ⚠️ Список полей и подписи приходят С СЕРВЕРА (`one_c_refs`): семейство уже
 * описано таблицей `ONE_C_REF_FIELDS`, и вторая её копия здесь разъехалась бы
 * молча — подпись ни на что не влияет, кроме глаз человека.
 */
export function TechRefsCard({
  data,
}: {
  data: SystemSettingsView;
}): React.ReactElement {
  const qc = useQueryClient();
  const rows = data.one_c_refs;
  const [picked, setPicked] = useState<Record<string, PickerRow | null>>({});

  useEffect(() => {
    setPicked(
      Object.fromEntries(
        data.one_c_refs.map((r) => [r.column, savedPickerValue(r.ref, r.name)]),
      ),
    );
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateSystemSettings({
        one_c_refs: Object.fromEntries(
          rows.map((r) => [
            r.column,
            { ref: picked[r.column]?.ref ?? "", name: picked[r.column]?.name ?? "" },
          ]),
        ),
      }),
    onSuccess: () => {
      toast.success("Ссылки 1С сохранены");
      void qc.invalidateQueries({ queryKey: ["system-settings"] });
    },
    onError: toastError("Не удалось сохранить"),
  });

  const missing = rows.filter((r) => !picked[r.column]).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Технические ссылки 1С</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Хозоперации, состояния заказов, валюта и единица «шт». У каждой базы
          свои — выбираются из справочника, руками не вводятся. Незаполненная
          ссылка останавливает создание документа с явной ошибкой.
        </p>
        {missing > 0 && (
          <p className="text-sm text-amber-700">Не заполнено: {missing}.</p>
        )}
        {rows.map((row) => (
          <PickerField
            key={row.column}
            label={row.title}
            value={picked[row.column] ?? null}
            onChange={(v) => setPicked((prev) => ({ ...prev, [row.column]: v }))}
            search={(q) => searchSettingRefs(row.kind, q)}
            queryKey={["one-c-ref", row.kind]}
            placeholder="Выберите из справочника 1С"
          />
        ))}
        <Button onClick={() => save.mutate()} disabled={save.isPending || !rows.length}>
          Сохранить
        </Button>
      </CardContent>
    </Card>
  );
}
